import { combine, createEffect, createEvent, createStore, sample } from 'effector';
import type { ModuleId, TenantBranding } from '@buildex/module-contract';
import { loginFx, restoreSessionFx } from '@buildex/core';
import type { Session } from '@buildex/core';
import {
  createTenant,
  fetchTenants,
  setTenantActive,
  setTenantModule,
} from './api';
import type { AdminTenant, CreateTenantParams, TenantRole, TenantUser } from './types';

// --- загрузка списка -----------------------------------------------------

export const fetchTenantsFx = createEffect<void, AdminTenant[], Error>(fetchTenants);

export const $tenants = createStore<AdminTenant[]>([]).on(
  fetchTenantsFx.doneData,
  (_, tenants) => tenants,
);

export const $tenantsLoading = fetchTenantsFx.pending;

// Есть сессия админа → грузим тенантов
sample({
  clock: [restoreSessionFx.doneData, loginFx.doneData],
  filter: (session: Session | null): session is Session => session !== null,
  fn: (): void => undefined,
  target: fetchTenantsFx,
});

// --- создание / блокировка -------------------------------------------------

export const tenantCreateSubmitted = createEvent<CreateTenantParams>();

export const createTenantFx = createEffect<CreateTenantParams, AdminTenant, Error>(createTenant);

export const $tenantCreating = createTenantFx.pending;

sample({ clock: tenantCreateSubmitted, target: createTenantFx });

$tenants.on(createTenantFx.doneData, (tenants, tenant) => [...tenants, tenant]);

export const tenantActiveToggled = createEvent<{ tenantId: string; active: boolean }>();

export const setTenantActiveFx = createEffect<
  { tenantId: string; active: boolean },
  { tenantId: string; active: boolean },
  Error
>(({ tenantId, active }) => setTenantActive(tenantId, active));

sample({ clock: tenantActiveToggled, target: setTenantActiveFx });

$tenants.on(setTenantActiveFx.doneData, (tenants, { tenantId, active }) =>
  tenants.map((t) => (t.id === tenantId ? { ...t, active } : t)),
);

// --- текущий тенант (деталка) ----------------------------------------------

export const tenantOpened = createEvent<string>();

export const $currentTenantId = createStore<string | null>(null).on(
  tenantOpened,
  (_, id) => id,
);

export const $currentTenant = combine(
  $tenants,
  $currentTenantId,
  (tenants, id) => tenants.find((t) => t.id === id) ?? null,
);

// --- модули тенанта ---------------------------------------------------------

export const tenantModuleToggled = createEvent<{
  tenantId: string;
  moduleId: ModuleId;
  enabled: boolean;
}>();

export const setTenantModuleFx = createEffect<
  { tenantId: string; moduleId: ModuleId; enabled: boolean },
  { tenantId: string; moduleId: ModuleId; enabled: boolean },
  Error
>(({ tenantId, moduleId, enabled }) => setTenantModule(tenantId, moduleId, enabled));

sample({ clock: tenantModuleToggled, target: setTenantModuleFx });

$tenants.on(setTenantModuleFx.doneData, (tenants, { tenantId, moduleId, enabled }) =>
  tenants.map((t) =>
    t.id === tenantId
      ? {
          ...t,
          modules: enabled
            ? [...new Set([...t.modules, moduleId])]
            : t.modules.filter((m) => m !== moduleId),
        }
      : t,
  ),
);

// --- брендинг (применение сохранённого) -------------------------------------

export const tenantBrandingSaved = createEvent<{ tenantId: string; branding: TenantBranding }>();

$tenants.on(tenantBrandingSaved, (tenants, { tenantId, branding }) =>
  tenants.map((t) => (t.id === tenantId ? { ...t, branding } : t)),
);

// --- пользователи и роли (моки, локальные события) ---------------------------

export const userInvited = createEvent<{ tenantId: string; email: string; roleId: string }>();

$tenants.on(userInvited, (tenants, { tenantId, email, roleId }) =>
  tenants.map((t) =>
    t.id === tenantId
      ? {
          ...t,
          users: [
            ...t.users,
            {
              id: `u-${Date.now()}`,
              email,
              name: '—',
              roleId,
              status: 'invited',
            } satisfies TenantUser,
          ],
        }
      : t,
  ),
);

export const roleCreated = createEvent<{ tenantId: string; name: string; permissions: string[] }>();

$tenants.on(roleCreated, (tenants, { tenantId, name, permissions }) =>
  tenants.map((t) =>
    t.id === tenantId
      ? {
          ...t,
          roles: [
            ...t.roles,
            { id: `r-${Date.now()}`, name, permissions } satisfies TenantRole,
          ],
        }
      : t,
  ),
);

export const roleDeleted = createEvent<{ tenantId: string; roleId: string }>();

$tenants.on(roleDeleted, (tenants, { tenantId, roleId }) =>
  tenants.map((t) =>
    t.id === tenantId ? { ...t, roles: t.roles.filter((r) => r.id !== roleId) } : t,
  ),
);
